import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';

function Services() {
  const [services, setServices] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', duration: '', price: '', starting_at_price: false });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    const response = await axios.get(`${API_URL}/services`);
    setServices(response.data);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check file size on client side before uploading
    if (selectedImage && selectedImage.size > 5 * 1024 * 1024) {
      alert('Image file is too large. Maximum size is 5MB. Please choose a smaller image.');
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('name', formData.name);
    formDataToSend.append('description', formData.description);
    formDataToSend.append('duration', formData.duration);
    formDataToSend.append('price', formData.price);
    formDataToSend.append('starting_at_price', formData.starting_at_price ? 1 : 0);
    formDataToSend.append('active', 1);

    if (selectedImage) {
      formDataToSend.append('image', selectedImage);
    }

    try {
      if (editingService) {
        await axios.put(`${API_URL}/services/${editingService.id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post(`${API_URL}/services`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      setShowModal(false);
      setEditingService(null);
      setFormData({ name: '', description: '', duration: '', price: '', starting_at_price: false });
      setSelectedImage(null);
      setImagePreview(null);
      fetchServices();
    } catch (error) {
      console.error('Error saving service:', error);
      if (error.response?.status === 413) {
        alert('Image file is too large. The server/nginx limit has been exceeded. Please choose a smaller image or contact your administrator to increase the upload limit.');
      } else if (error.message.includes('Only image files are allowed')) {
        alert('Only image files (JPEG, PNG, GIF, WebP) are allowed.');
      } else {
        alert('Failed to save service: ' + (error.response?.data?.error || error.message));
      }
    }
  };

  const deleteService = async (id) => {
    if (window.confirm('Deactivate this service?')) {
      await axios.delete(`${API_URL}/services/${id}`);
      fetchServices();
    }
  };

  return (
    <div className="services">
      <div className="header">
        <h2>Services</h2>
        <button onClick={() => setShowModal(true)}>Add Service</button>
      </div>
      <div className="services-grid">
        {services.map(service => (
          <div key={service.id} className="service-card">
            <h3>{service.name}</h3>
            <p>{service.description}</p>
            <div className="service-details">
              <span>${service.price}{service.starting_at_price ? '+' : ''}</span>
              <span>{service.duration} min</span>
            </div>
            <div className="service-actions">
              <button onClick={() => { setEditingService(service); setFormData(service); setShowModal(true); }}>Edit</button>
              <button onClick={() => deleteService(service.id)} className="delete">Remove</button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal">
          <div className="modal-content large">
            <h3>{editingService ? 'Edit Service' : 'Add Service'}</h3>
            <form onSubmit={handleSubmit}>
              <input type="text" placeholder="Service Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
              <textarea placeholder="Description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
              <input type="number" placeholder="Duration (minutes)" value={formData.duration} onChange={(e) => setFormData({...formData, duration: e.target.value})} required />
              <input type="number" step="0.01" placeholder="Price" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} required />

              <div className="form-group" style={{ marginTop: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.starting_at_price || false}
                    onChange={(e) => setFormData({...formData, starting_at_price: e.target.checked})}
                    style={{ marginRight: '8px' }}
                  />
                  Display as "Starting at" price (adds + symbol)
                </label>
                <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                  Example: ${formData.price || '0.00'}{formData.starting_at_price ? '+' : ''}
                </small>
              </div>

              <div className="form-group">
                <label>Service Image (Optional - Max 5MB)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                {selectedImage && (
                  <p className="file-info" style={{
                    color: selectedImage.size > 5 * 1024 * 1024 ? 'var(--danger)' : 'var(--text-light)',
                    fontSize: '0.85rem',
                    marginTop: '5px'
                  }}>
                    File size: {(selectedImage.size / 1024 / 1024).toFixed(2)} MB
                    {selectedImage.size > 5 * 1024 * 1024 && ' (Too large!)'}
                  </p>
                )}
                {imagePreview && (
                  <div className="image-preview">
                    <img src={imagePreview} alt="Preview" />
                  </div>
                )}
                {!imagePreview && editingService && editingService.image_url && (
                  <div className="image-preview">
                    <img src={editingService.image_url} alt="Current" />
                    <p className="image-label">Current Image</p>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="submit">Save</button>
                <button type="button" onClick={() => {
                  setShowModal(false);
                  setEditingService(null);
                  setFormData({ name: '', description: '', duration: '', price: '', starting_at_price: false });
                  setSelectedImage(null);
                  setImagePreview(null);
                }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Services;
